import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  accountApi,
  type AccountUser,
  type LoginInput,
  type RegisterInput,
} from '@/lib/account';

interface AuthState {
  user: AccountUser | null;
  loading: boolean;
  signIn: (input: LoginInput) => Promise<AccountUser>;
  signUp: (input: RegisterInput) => Promise<AccountUser>;
  signOut: () => Promise<void>;
  updateName: (name: string) => Promise<AccountUser>;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AccountUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const next = await accountApi.session();
    setUser(next);
  }, []);

  useEffect(() => {
    let cancelled = false;
    accountApi
      .session()
      .then((next) => {
        if (!cancelled) setUser(next);
      })
      .catch(() => {
        if (!cancelled) setUser(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (input: LoginInput) => {
    const next = await accountApi.login(input);
    setUser(next);
    return next;
  }, []);

  const signUp = useCallback(async (input: RegisterInput) => {
    const next = await accountApi.register(input);
    setUser(next);
    return next;
  }, []);

  const signOut = useCallback(async () => {
    await accountApi.logout();
    setUser(null);
  }, []);

  const updateName = useCallback(async (name: string) => {
    const next = await accountApi.updateProfile(name);
    setUser(next);
    return next;
  }, []);

  const value = useMemo<AuthState>(
    () => ({ user, loading, signIn, signUp, signOut, updateName, refresh }),
    [user, loading, signIn, signUp, signOut, updateName, refresh],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}
